

































































































































































































import { Vue, Prop, Component, Watch } from 'vue-property-decorator';
import { Retailer, NewMeetingSlot, SellerInfo } from '@/models/BookMeetingFlow';
import { websiteText } from '@/mixins/websiteTexts';
import { CustomField, CustomFieldData } from '@/models/Contact';
import { FileUpload } from '@/models/FileUpload';
import { TextValuePair } from '@/models/TextValuePair';
import DynamicForm from '@/components/Form/DynamicForm.vue';
import { getKitchenLabId } from '@/mixins/kitchenLabId';
import IdentityVerification from '@/components/Retailer/IdentityVerification.vue';
import { meetingFetch, meetingLog, meetingWarn, meetingTrack, MeetingTrackEvent, createMeetingFlowId } from '@/services/MeetingBookingLog';

interface TimeGroup {
    timeKey: string;
    startTime: string;
    endTime: string;
    sellerIds: string[];
    slots: NewMeetingSlot[];
}

@Component({
    components: {
        DynamicForm,
        IdentityVerification
    }
})
class NewMeetingBookingFlow extends Vue {
    [x: string]: any;
    @Prop() retailer!: Retailer;
    @Prop() meetingType!: string;
    @Prop() currentCulture!: string;
    @Prop({ default: null }) initialOrganizationId!: string | null;
    @Prop({ type: String, default: 'unknown' }) bookingSource!: string;

    loading = false;
    error: string | null = null;
    storeId: string | null = null;
    firstBookableDate: string | null = null;
    allSlots: NewMeetingSlot[] = [];
    sellers: Record<string, SellerInfo> = {};
    weekOffset = 0;
    selectedDate: string | null = null;
    selectedSlot: TimeGroup | null = null;
    selectedSellerId: string | null = null;

    submitting = false;
    bookingError: string | null = null;
    confirmed = false;
    bookedMeetingId: string | null = null;

    additionalInfoForm = { colorSamples: '', countertopMaterials: '', images: [] as File[] };
    additionalInfoSubmitting = false;
    additionalInfoError: string | null = null;
    additionalInfoDone = false;

    form = {
        customerFirstName: '',
        customerLastName: '',
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        customerPostalCode: '',
        customerCity: '',
        customerMessage: '',
        images: [] as FileUpload[],
    };

    homeForm = {
        customerFirstName: '',
        customerLastName: '',
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        customerPostalCode: '',
        customerCity: '',
        preferredTimes: '',
        preferredContactMethod: 0,
        message: '',
        images: [] as FileUpload[],
    };

    // ── Home visit: multi-step state ─────────────────────────────────────────
    homeStep: 'form' | 'bankId' = 'form';
    homeVisitMeetingId: string | null = null;

    // BankID
    bankIdStarting = false;
    bankIdPolling = false;
    bankIdShowQr = false;
    bankIdQrImage = '';
    bankIdQrTimestamp = 1;
    bankIdError: string | null = null;

    // Additional info
    homeAdditionalInfo = {
        colorSamples: [] as string[],
        countertopMaterials: [] as string[],
        images: [] as File[],
    };
    homeAdditionalInfoSubmitting = false;
    homeAdditionalInfoError: string | null = null;

    // Server-side funnel logging (MeetingBooking.log) — see meetingTrack
    flowId = createMeetingFlowId();
    flowStartedAt = Date.now();
    // Set once 'completed' or 'abandoned' has been sent, so a flow gets exactly one outcome.
    outcomeReported = false;
    leftPageForBankId = false;

    get flowStep(): string {
        if (this.confirmed) return 'confirmed';
        if (this.isHomeVisit) return this.homeStep === 'form' ? 'home_form' : 'bankid';
        if (this.loading) return 'loading_times';
        if (this.error) return 'times_error';
        return this.selectedSlot ? 'contact_form' : 'time_picker';
    }

    /** The customer's KitchenLab design, see mixins/kitchenLabId.ts. */
    get kitchenLabId(): string | null {
        return getKitchenLabId();
    }

    /** Shown in the booking forms only when the customer arrives from KitchenLab. */
    get kitchenLabNotice(): string {
        const key = 'meeting.kitchenlab.attached';
        const text = websiteText(key);
        if (text && text.toLowerCase() !== key) {
            return text;
        }
        // Website text not created in Litium yet - fall back on the page culture.
        return /^n[bo]/i.test(this.currentCulture || '')
            ? 'Kjøkkenet du har designet i KitchenLab sendes med bestillingen.'
            : 'Köket du har designat i KitchenLab skickas med din bokning.';
    }

    track(event: MeetingTrackEvent, message?: string | null) {
        if (event === 'completed') this.outcomeReported = true;
        meetingTrack(event, {
            flowId: this.flowId,
            source: this.bookingSource,
            meetingType: this.meetingType,
            organizationId: this.retailer ? this.retailer.organizationSystemId : null,
            step: this.flowStep,
            meetingId: this.bookedMeetingId || this.homeVisitMeetingId,
            message: message || null,
            elapsedMs: Date.now() - this.flowStartedAt
        });
    }

    reportAbandoned(trigger: string) {
        if (this.confirmed || this.outcomeReported) return;
        this.outcomeReported = true;
        const lastError = this.bookingError || this.error;
        this.track('abandoned', trigger + (lastError ? ' — last error shown: ' + lastError : ''));
    }

    @Watch('confirmed')
    onConfirmedChanged(newVal: boolean) {
        if (newVal) {
            this.matomoEvent("Meeting booked (new)", this.retailer.name, this.meetingType);
        }
    }

    // Tab closed, reload or navigation away while the flow is open. beforeDestroy does notF
    // run for those, so this is the only chance to report them.
    onPageHide() {
        // nb-NO same-device BankID leaves the page on purpose and comes back via the return url.
        if (this.leftPageForBankId && this.currentCulture === 'nb-NO') return;
        this.reportAbandoned('page closed or left');
    }

    beforeDestroy() {
        window.removeEventListener('pagehide', this.onPageHide);
        // The modal was closed, or the customer went back to pick another retailer/meeting type.
        this.reportAbandoned('flow closed or went back');
    }

    onBankIdStarted(getQr: boolean) {
        this.leftPageForBankId = !getQr;
        this.track('bankid_started', getQr ? 'qr/other device' : 'same device');
    }

    onBankIdCancelled() {
        this.track('bankid_cancelled');
    }

    onBankIdFailed(reason: string) {
        this.track('bankid_failed', reason);
    }

    get commonFields(): CustomField[] {
        return [
            { fieldType: 'TextField', placeholder: 'Förnamn', mandatory: true, tabIndex: 1, autocomplete: 'given-name' },
            { fieldType: 'TextField', placeholder: 'Efternamn', mandatory: true, tabIndex: 2, autocomplete: 'family-name' },
            { fieldType: 'EmailField', placeholder: 'E-post', mandatory: true, tabIndex: 3, autocomplete: 'email' },
            { fieldType: 'TextField', placeholder: 'Telefon', mandatory: true, tabIndex: 4, autocomplete: 'tel' },
            { fieldType: 'TextField', placeholder: 'Adress', mandatory: false, tabIndex: 5, autocomplete: 'street-address' },
            { fieldType: 'TextField', placeholder: 'Postnummer', mandatory: false, tabIndex: 6, autocomplete: 'postal-code' },
            { fieldType: 'TextField', placeholder: 'Stad', mandatory: false, tabIndex: 7, autocomplete: 'address-level2' },
        ];
    }

    get messageAndFilesFields(): CustomField[] {
        return [
            { fieldType: 'MultirowTextField', placeholder: 'Meddelande', mandatory: false, tabIndex: 8 },
            { fieldType: 'FileUploadField', placeholder: 'Bilder (valfritt)', mandatory: false, tabIndex: 9, acceptedFiles: 'image/*' }
        ];
    }

    get colorSamples(): { text: string; value: string }[] {
        return websiteText('retailer.bookingform.home.colorsamples').split(',').map((s: string) => ({ text: s, value: s })) || [];
    }
    get countertopMaterials(): { text: string; value: string }[] {
        return websiteText('retailer.bookingform.home.countertopmaterials').split(',').map((s: string) => ({ text: s, value: s })) || [];
    }

    get additionalInfoFields(): CustomField[] {
        return [
            {
                fieldType: 'CheckboxField',
                placeholder: websiteText('meeting.color.samples'),
                mandatory: false,
                tabIndex: 1,
                autocomplete: 'off',
                options: this.colorSamples,
            },
            {
                fieldType: 'CheckboxField',
                placeholder: websiteText('meeting.countertop.material'),
                mandatory: false,
                tabIndex: 2,
                autocomplete: 'off',
                options: this.countertopMaterials,
            },
        ];
    }

    get storeBookingFields(): CustomField[] {
        return [
            ...this.commonFields,
            ...this.messageAndFilesFields
        ];
    }

    get homeBookingFields(): CustomField[] {
        return [
            ...this.commonFields,
            { 
                fieldType: 'OptionField', 
                placeholder: 'Önskad tidpunkt för mötet', 
                mandatory: false, 
                options: [
                    {value: '', text: 'Välj tidpunkt'},
                    {value: 'Vardag - dagtid', text: 'Vardag - dagtid'},
                    {value: 'Vardag - kvällstid', text: 'Vardag - kvällstid'},
                    {value: 'Jag är flexibel avseende tid', text: 'Jag är flexibel avseende tid'}] as TextValuePair[],
                tabIndex: 8
            },
            { 
                fieldType: 'OptionField', 
                placeholder: 'Hur föredrar du att bli kontaktad?', 
                mandatory: false, 
                options: [
                    {value: '0', text: 'Välj kontaktsätt'},
                    {value: '1', text: 'Telefonsamtal'},
                    {value: '2', text: 'SMS'},
                    {value: '3', text: 'E-post'}
                ] as TextValuePair[],
                tabIndex: 9
            },
            ...this.additionalInfoFields,
            ...this.messageAndFilesFields
        ];
    }

    private getFieldValue(data: CustomFieldData[], placeholder: string): string {
        const field = data.find(d => d.title === placeholder);
        return field ? field.value : '';
    }

    // The FileUploadField (see FilesUploader.vue) uploads each file to /api/fileupload/ as soon
    // as it's picked and only ever hands back FileUpload metadata (fileUrl, fileName, ...) —
    // never the original File/Blob. We can't reliably re-fetch fileUrl as a Blob from the
    // browser (the media storage origin, e.g. a shared test/CDN domain, may not send
    // Access-Control-Allow-Origin), so we pass the metadata through as-is and send it as URL
    // references for the Litium backend to resolve server-side (no CORS there).
    private getFieldFiles(data: CustomFieldData[], placeholder: string): FileUpload[] {
        const field = data.find(d => d.title === placeholder);
        return field && field.files ? field.files : [];
    }

    async onStoreFormSubmit(formData: CustomFieldData[]) {
        if (!this.selectedSlot || !this.storeId || !this.selectedDate) return;

        this.submitting = true;
        this.bookingError = null;

        const files = this.getFieldFiles(formData, 'Bilder (valfritt)');
        if (files.length > 0) {
            this.additionalInfoDone = true;
            this.form.images = files;
        }

        const sellerId = this.selectedSellerId !== null ? this.selectedSellerId : this.selectedSlot.sellerIds[0];

        const body = {
            sellerId: sellerId,
            storeId: this.storeId,
            type: this.meetingType,
            day: this.selectedDate,
            startTime: this.selectedSlot.startTime,
            endTime: this.selectedSlot.endTime,
            customerName: (this.getFieldValue(formData, 'Förnamn') + ' ' + this.getFieldValue(formData, 'Efternamn')).trim(),
            customerEmail: this.getFieldValue(formData, 'E-post'),
            customerPhone: this.getFieldValue(formData, 'Telefon') || null,
            customerAddress: this.getFieldValue(formData, 'Adress') || null,
            customerPostalCode: this.getFieldValue(formData, 'Postnummer') || null,
            customerCity: this.getFieldValue(formData, 'Stad') || null,
            customerMessage: this.getFieldValue(formData, 'Meddelande') || null,
            source: this.bookingSource,
            kitchenLabId: this.kitchenLabId,
        };

        const result = await this.postJson('/api/publicmeetingbooking/book', body);
        if (result && (result as any).id) {
            this.bookedMeetingId = (result as any).id;
        }
        if (this.confirmed) {
            this.track('completed', files.length + ' images');
        }
        if (this.bookedMeetingId && files.length > 0) {
            await this.submitStoreVideoImages();
        }
    }

    async onHomeFormSubmit(formData: CustomFieldData[]) {
        this.submitting = true;
        this.bookingError = null;

        const files = this.getFieldFiles(formData, 'Bilder (valfritt)');
        this.homeForm.images = files;

        const contactMethodMap: Record<string, number> = {
            'Telefonsamtal': 2,
            'SMS': 1,
            'E-post': 3
        };
        const selectedContact = this.getFieldValue(formData, 'Hur föredrar du att bli kontaktad?');
        const contactMethodInt = contactMethodMap[selectedContact] || 0;

        const body = {
            organizationId: this.retailer.organizationSystemId,
            customerFirstName: this.getFieldValue(formData, 'Förnamn'),
            customerLastName: this.getFieldValue(formData, 'Efternamn'),
            customerEmail: this.getFieldValue(formData, 'E-post'),
            customerPhone: this.getFieldValue(formData, 'Telefon') || null,
            customerAddress: this.getFieldValue(formData, 'Adress') || null,
            customerPostalCode: this.getFieldValue(formData, 'Postnummer') || null,
            customerCity: this.getFieldValue(formData, 'Stad') || null,
            preferredTimes: this.getFieldValue(formData, 'Önskad tidpunkt för mötet') || null,
            preferredContactMethod: contactMethodInt,
            message: this.getFieldValue(formData, 'Meddelande') || null,
            colorSamples: this.getFieldValue(formData, websiteText('meeting.color.samples')) || null,
            countertopMaterials: this.getFieldValue(formData, websiteText('meeting.countertop.material')) || null,
            source: this.bookingSource,
            kitchenLabId: this.kitchenLabId,
        };

        try {
            const litium = (window as any).__litium;
            const requestContext = litium ? litium.requestContext : null;
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (requestContext) headers['litium-request-context'] = JSON.stringify(requestContext);

            const response = await meetingFetch('homevisit', '/api/publicmeetingbooking/homevisit', {
                method: 'POST',
                headers,
                body: JSON.stringify(body),
            });

            if (response.ok) {
                const data = await response.json().catch(() => null);
                this.homeVisitMeetingId = typeof data === 'string' ? data : (data && data.id ? data.id : null);
                this.homeStep = 'bankId';
            } else {
                const err = await response.json().catch(() => null);
                this.bookingError = (err && err.detail) || (err && err.title) || 'Misslyckades. Försök igen.';
                this.track('booking_failed', 'status ' + response.status + ': ' + this.bookingError);
            }
        } catch (e) {
            this.bookingError = 'Misslyckades. Försök igen.';
            this.track('booking_failed', 'no response (network or script error): ' + e);
        } finally {
            this.submitting = false;
        }
    }


    get isHomeVisit(): boolean {
        return this.meetingType === 'home';
    }

    get isStoreVisit(): boolean {
        return this.meetingType === 'store'
    }

    get colorSampleOptions(): string[] {
        return websiteText('retailer.bookingform.home.colorsamples')
            .split(',').map((s: string) => s.trim()).filter(Boolean);
    }

    get countertopMaterialOptions(): string[] {
        return websiteText('retailer.bookingform.home.countertopmaterials')
            .split(',').map((s: string) => s.trim()).filter(Boolean);
    }

    get meetingTypeLabel(): string {
        const labels: Record<string, string> = {
            store: 'Butiksmöte',
            video: 'Videomöte',
            home: 'Hembesök',
        };
        return labels[this.meetingType] || this.meetingType;
    }

    get fromDate(): string {
        const d = new Date();
        d.setDate(d.getDate() + this.weekOffset * 7);
        return this.formatDate(d);
    }

    get toDate(): string {
        const d = new Date();
        d.setDate(d.getDate() + this.weekOffset * 7 + 13);
        return this.formatDate(d);
    }

    get visibleDays(): Array<{ iso: string; label: string; weekday: string; hasSlots: boolean }> {
        const days = [];
        const base = new Date();
        base.setDate(base.getDate() + this.weekOffset * 7);
        const slotDates = new Set(this.allSlots.map(s => s.start.substring(0, 10)));

        for (let i = 0; i < 7; i++) {
            const d = new Date(base);
            d.setDate(d.getDate() + i);
            const iso = this.formatDate(d);
            days.push({
                iso,
                label: d.getDate() + '/' + (d.getMonth() + 1),
                weekday: ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'][d.getDay()],
                hasSlots: slotDates.has(iso),
            });
        }
        return days;
    }

    get slotsForSelectedDate(): NewMeetingSlot[] {
        if (!this.selectedDate) return [];
        return this.allSlots.filter(s => s.start.substring(0, 10) === this.selectedDate);
    }

    get groupedSlots(): TimeGroup[] {
        const groups = new Map<string, TimeGroup>();
        for (const slot of this.slotsForSelectedDate) {
            const startTime = slot.start.substring(11, 16);
            const endTime = slot.end.substring(11, 16);
            const key = startTime + '-' + endTime;
            if (!groups.has(key)) {
                groups.set(key, { timeKey: key, startTime, endTime, sellerIds: [], slots: [] });
            }
            const g = groups.get(key)!;
            // Deduplicate: a seller can appear only once per time group
            if (g.sellerIds.indexOf(slot.sellerId) === -1) {
                g.sellerIds.push(slot.sellerId);
            }
            g.slots.push(slot);
        }
        return Array.from(groups.values()).sort((a, b) => a.timeKey.localeCompare(b.timeKey));
    }

    get selectedDateLabel(): string {
        if (!this.selectedDate) return '';
        const d = new Date(this.selectedDate);
        const weekdays = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];
        return weekdays[d.getDay()] + ' ' + d.getDate() + '/' + (d.getMonth() + 1);
    }

    async created() {
        meetingLog('NewMeetingBookingFlow', 'started for meeting type "' + this.meetingType + '"', {
            retailer: this.retailer.name,
            organizationSystemId: this.retailer.organizationSystemId,
            availableMeetingTypes: this.retailer.availableMeetingTypes,
            isHomeVisit: this.isHomeVisit,
            isStoreVisit: this.isStoreVisit

        });

        this.track('started');
        window.addEventListener('pagehide', this.onPageHide);

        if (!this.isHomeVisit) {
            await this.fetchAvailability();
        }
    }

    // This function is called in selectedItem.vue so we can reuse the same back button
    handleBack() {
        if (this.selectedSlot) {
            this.selectedSlot = null;
            this.selectedSellerId = null;
        } else {
            this.$emit('back');
        }
    }

    async fetchAvailability() {
        this.loading = true;
        this.error = null;
        try {
            const orgId = this.retailer.organizationSystemId;
            const url = '/api/publicmeetingbooking/availability?organizationId=' + orgId +
                '&meetingType=' + this.meetingType +
                '&from=' + this.fromDate +
                '&to=' + this.toDate;

            const litium = (window as any).__litium;
            const requestContext = litium ? litium.requestContext : null;
            const headers: Record<string, string> = {};
            if (requestContext) headers['litium-request-context'] = JSON.stringify(requestContext);

            const response = await meetingFetch('availability', url, { headers: headers });
            if (!response.ok) {
                meetingWarn('NewMeetingBookingFlow',
                    'availability request failed with status ' + response.status +
                    ' — 404 means the meeting api has no store for this litium organizationSystemId');
                this.error = 'Kunde inte hämta tillgängliga tider. Försök igen.';
                this.track('availability_failed', 'status ' + response.status + ' for ' + this.fromDate + '–' + this.toDate);
                return;
            }
            const data = await response.json();
            
            this.firstBookableDate = data.firstBookableDate || null;
            this.storeId = data.storeId;
            this.allSlots = data.times || [];
            this.sellers = data.sellers || {};

            meetingLog('NewMeetingBookingFlow', 'availability for ' + this.meetingType + ' ' +
                this.fromDate + '–' + this.toDate + ': ' + this.allSlots.length + ' slots', {
                organizationSystemId: this.retailer.organizationSystemId,
                storeIdInMeetingApi: this.storeId,
                sellers: Object.keys(this.sellers).length,
                daysWithSlots: this.allSlots.map(s => s.start.substring(0, 10)).filter((v, i, a) => a.indexOf(v) === i)
            });
            if (this.allSlots.length === 0) {
                meetingWarn('NewMeetingBookingFlow',
                    'the store was found in the meeting api but has no ' + this.meetingType +
                    ' slots in this date range — the customer gets an empty calendar');
            }

            if (!this.selectedDate) {
                const firstWithSlots = this.visibleDays.find(function(d) { return d.hasSlots; });
                this.selectedDate = firstWithSlots ? firstWithSlots.iso : this.visibleDays[0].iso;
            }
        } catch (e) {
            this.error = 'Kunde inte hämta tillgängliga tider. Försök igen.';
            this.track('availability_failed', 'no response (network or script error): ' + e);
        } finally {
            this.loading = false;
        }
    }

    async prevWeek() {
        if (this.weekOffset === 0) return;
        this.weekOffset--;
        this.selectedDate = null;
        await this.fetchAvailability();
    }

    async nextWeek() {
        this.weekOffset++;
        this.selectedDate = null;
        await this.fetchAvailability();
    }

    // Row click — only advances for single-seller groups.
    onSlotRowClick(group: TimeGroup) {
        if (group.sellerIds.length === 1) {
            this.selectedSellerId = group.sellerIds[0];
            this.selectedSlot = group;
            this.bookingError = null;
        }
        // Multi-seller: customer must click a specific seller chip instead.
    }

    // Chip click — always picks this seller and advances to the contact form.
    onSellerChipClick(group: TimeGroup, sellerId: string) {
        this.selectedSellerId = sellerId;
        this.selectedSlot = group;
        this.bookingError = null;
        this.track('slot_selected', this.selectedDate + ' ' + group.startTime + '-' + group.endTime + ', seller ' + sellerId);
    }

    // Returns the name to display on a seller chip.
    // ≥4 sellers in the slot → first name only to save space.
    sellerDisplayName(sellerId: string, totalSellers: number): string {
        const info = this.sellers[sellerId];
        if (!info) { return sellerId.substring(0, 8); }
        if (totalSellers >= 4) {
            const spaceIdx = info.name.indexOf(' ');
            return spaceIdx > -1 ? info.name.substring(0, spaceIdx) : info.name;
        }
        return info.name;
    }

    // The alternative display name the seller set on the time slot behind this time, if any (BALM-181).
    // Shown instead of the seller's name, with the seller's name underneath in smaller text.
    sellerAltDisplayName(group: TimeGroup, sellerId: string): string | null {
        for (const slot of group.slots) {
            const name = slot.sellerId === sellerId && slot.altDisplayName ? slot.altDisplayName.trim() : '';
            if (name) { return name; }
        }
        return null;
    }

    /** @deprecated Use onSlotRowClick instead */
    selectSlot(group: TimeGroup) {
        this.onSlotRowClick(group);
    }

    // POST /api/meetings (store + video)
    // async submitBooking() {
    //     if (!this.selectedSlot || !this.storeId || !this.selectedDate) return;

    //     this.submitting = true;
    //     this.bookingError = null;

    //     if (this.form.images.length > 0) {
    //         this.additionalInfoDone = true;
    //     }

    //     const sellerId = this.selectedSellerId !== null ? this.selectedSellerId : this.selectedSlot.sellerIds[0];

    //     const body = {
    //         sellerId: sellerId,
    //         storeId: this.storeId,
    //         type: this.meetingType,
    //         day: this.selectedDate,
    //         startTime: this.selectedSlot.startTime,
    //         endTime: this.selectedSlot.endTime,
    //         customerName: (this.form.customerFirstName + ' ' + this.form.customerLastName).trim(),
    //         customerEmail: this.form.customerEmail,
    //         customerPhone: this.form.customerPhone || null,
    //         customerAddress: this.form.customerAddress || null,
    //         customerPostalCode: this.form.customerPostalCode || null,
    //         customerCity: this.form.customerCity || null,
    //         customerMessage: this.form.customerMessage || null,
    //     };

    //     const result = await this.postJson('/api/publicmeetingbooking/book', body);
    //     if (result && (result as any).id) {
    //         this.bookedMeetingId = (result as any).id;
    //         if (this.form.images.length > 0) {
    //             await this.submitStoreVideoImages();
    //         }
    //     }
    // }

    // POST /api/publicmeetingbooking/homevisit — step 1 of home visit flow
    // async submitHomeVisit() {
    //     this.submitting = true;
    //     this.bookingError = null;

    //     const body = {
    //         organizationId: this.retailer.organizationSystemId,
    //         customerFirstName: this.homeForm.customerFirstName,
    //         customerLastName: this.homeForm.customerLastName,
    //         customerEmail: this.homeForm.customerEmail,
    //         customerPhone: this.homeForm.customerPhone || null,
    //         customerAddress: this.homeForm.customerAddress || null,
    //         customerPostalCode: this.homeForm.customerPostalCode || null,
    //         customerCity: this.homeForm.customerCity || null,
    //         preferredTimes: this.homeForm.preferredTimes || null,
    //         preferredContactMethod: this.homeForm.preferredContactMethod,
    //         message: this.homeForm.message || null,
    //     };

    //     try {
    //         const litium = (window as any).__litium;
    //         const requestContext = litium ? litium.requestContext : null;
    //         const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    //         if (requestContext) headers['litium-request-context'] = JSON.stringify(requestContext);

    //         const response = await fetch('/api/publicmeetingbooking/homevisit', {
    //             method: 'POST',
    //             headers,
    //             body: JSON.stringify(body),
    //         });

    //         if (response.ok) {
    //             // The endpoint returns the raw Guid as a JSON string, not { id: "..." }
    //             const data = await response.json().catch(() => null);
    //             this.homeVisitMeetingId = typeof data === 'string' ? data : (data && data.id ? data.id : null);
    //             this.homeStep = 'bankId';
    //         } else {
    //             const err = await response.json().catch(() => null);
    //             this.bookingError = (err && err.detail) || (err && err.title) || 'Misslyckades. Försök igen.';
    //         }
    //     } catch {
    //         this.bookingError = 'Misslyckades. Försök igen.';
    //     } finally {
    //         this.submitting = false;
    //     }
    // }

    // BankID — start auth (getQr=false: same device, getQr=true: QR code)
    async startBankId(getQr: boolean) {
        if (!this.homeVisitMeetingId) return;
        this.bankIdStarting = true;
        this.bankIdError = null;

        try {
            const litium = (window as any).__litium;
            const requestContext = litium ? litium.requestContext : null;
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (requestContext) headers['litium-request-context'] = JSON.stringify(requestContext);

            const response = await meetingFetch('bankid-auth', '/api/bankid/auth', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    meetingId: this.homeVisitMeetingId,
                    organizationId: this.retailer.organizationSystemId,
                    getQr,
                }),
            });

            if (!response.ok) {
                this.bankIdError = 'Kunde inte starta BankID. Försök igen.';
                return;
            }

            const data = await response.json();

            if (getQr) {
                this.bankIdQrImage = data.qrImage || '';
                this.bankIdShowQr = true;
                this.bankIdPolling = true;
                this.bankIdRefreshQr();
            } else {
                // Open BankID app without navigating away from the current page
                const autoStartToken = data.autoStartToken || '';
                const deepLink = `bankid:///?autostarttoken=${autoStartToken}&redirect=null`;
                window.open(deepLink, '_blank');
                this.bankIdShowQr = false;
                this.bankIdPolling = true;
            }

            this.bankIdPollCollect();
        } catch {
            this.bankIdError = 'Kunde inte starta BankID. Försök igen.';
        } finally {
            this.bankIdStarting = false;
        }
    }

    bankIdRefreshQr() {
        if (this.bankIdShowQr && this.bankIdPolling) {
            this.bankIdQrTimestamp++;
            setTimeout(() => this.bankIdRefreshQr(), 5000);
        }
    }

    cancelBankId() {
        this.bankIdPolling = false;
        this.bankIdShowQr = false;
        this.bankIdQrImage = '';
        this.bankIdQrTimestamp = 1;
    }

    bankIdPollCollect() {
        if (!this.bankIdPolling || !this.homeVisitMeetingId) return;

        const litium = (window as any).__litium;
        const requestContext = litium ? litium.requestContext : null;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (requestContext) headers['litium-request-context'] = JSON.stringify(requestContext);

        meetingFetch('bankid-collect', '/api/bankid/homevisit/collect', {
            method: 'POST',
            headers,
            body: JSON.stringify({
                meetingId: this.homeVisitMeetingId,
                organizationId: this.retailer.organizationSystemId,
            }),
        })
        .then(r => r.ok ? r.json() : Promise.reject(r))
        .then(data => {
            if (data.status === 'complete') {
                this.cancelBankId();
                this.submitHomeImages().then(() => { this.confirmed = true; });
            } else if (data.status === 'pending') {
                setTimeout(() => this.bankIdPollCollect(), 1500);
            } else {
                this.cancelBankId();
                this.bankIdError = data.errorMessage || 'BankID-verifieringen misslyckades. Försök igen.';
            }
        })
        .catch(() => {
            this.cancelBankId();
            this.bankIdError = 'Kunde inte kontakta BankID. Försök igen.';
        });
    }

    // Handler for IdentityVerification component
    async onBankIdVerified() {
        this.track('completed', this.homeForm.images.length + ' images');
        this.submitHomeImages().then(() => {
            this.confirmed = true;
        });
    }

    // Submit images for store/video — fires and forgets errors so they never block confirmation
    async submitStoreVideoImages() {
        if (!this.bookedMeetingId || this.form.images.length === 0) return;

        const formData = new FormData();
        for (const f of this.form.images) {
            if (!f.fileUrl) continue;
            formData.append('imageUrls[]', f.fileUrl);
            formData.append('imageFileNames[]', f.originalFileName || f.fileName || '');
        }

        try {
            const litium = (window as any).__litium;
            const requestContext = litium ? litium.requestContext : null;
            const headers: Record<string, string> = {};
            if (requestContext) headers['litium-request-context'] = JSON.stringify(requestContext);

            const response = await meetingFetch('store-video-images',
                `/api/publicmeetingbooking/additionalinfo/${this.bookedMeetingId}`,
                { method: 'POST', headers, body: formData }
            );
            if (!response.ok) {
                this.track('images_failed', 'status ' + response.status + ', ' + this.form.images.length + ' images');
            }
        } catch (e) {
            // Non-critical for the customer, but the store never sees the images.
            this.track('images_failed', 'no response (network or script error): ' + e);
        }
    }

    // Submit images after BankID completes — fires and forgets errors so they never block confirmation
    async submitHomeImages() {
        if (!this.homeVisitMeetingId || this.homeForm.images.length === 0) return;

        const formData = new FormData();
        for (const f of this.homeForm.images) {
            if (!f.fileUrl) continue;
            formData.append('imageUrls[]', f.fileUrl);
            formData.append('imageFileNames[]', f.originalFileName || f.fileName || '');
        }

        try {
            const litium = (window as any).__litium;
            const requestContext = litium ? litium.requestContext : null;
            const headers: Record<string, string> = {};
            if (requestContext) headers['litium-request-context'] = JSON.stringify(requestContext);

            const response = await meetingFetch('homevisit-images',
                `/api/publicmeetingbooking/homevisit/additionalinfo/${this.homeVisitMeetingId}`,
                { method: 'POST', headers, body: formData }
            );
            if (!response.ok) {
                this.track('images_failed', 'status ' + response.status + ', ' + this.homeForm.images.length + ' images');
            }
        } catch (e) {
            // Non-critical for the customer, but the store never sees the images.
            this.track('images_failed', 'no response (network or script error): ' + e);
        }
    }

    // Submit additional info for home visit — step 3
    async submitHomeAdditionalInfo() {
        if (!this.homeVisitMeetingId) { this.confirmed = true; return; }

        this.homeAdditionalInfoSubmitting = true;
        this.homeAdditionalInfoError = null;

        const formData = new FormData();
        for (const s of this.homeAdditionalInfo.colorSamples) formData.append('colorSamples[]', s);
        for (const m of this.homeAdditionalInfo.countertopMaterials) formData.append('countertopMaterials[]', m);
        for (const f of this.homeAdditionalInfo.images) formData.append('images', f, f.name);

        try {
            const litium = (window as any).__litium;
            const requestContext = litium ? litium.requestContext : null;
            const headers: Record<string, string> = {};
            if (requestContext) headers['litium-request-context'] = JSON.stringify(requestContext);

            const response = await meetingFetch('homevisit-additionalinfo',
                `/api/publicmeetingbooking/homevisit/additionalinfo/${this.homeVisitMeetingId}`,
                { method: 'POST', headers, body: formData }
            );

            if (response.ok) {
                this.confirmed = true;
            } else {
                this.homeAdditionalInfoError = 'Kunde inte skicka informationen. Du kan hoppa över och försöka igen senare.';
            }
        } catch {
            this.homeAdditionalInfoError = 'Kunde inte skicka informationen. Du kan hoppa över och försöka igen senare.';
        } finally {
            this.homeAdditionalInfoSubmitting = false;
        }
    }

    private async postJson(url: string, body: object): Promise<object | null> {
        try {
            const litium2 = (window as any).__litium;
            const requestContext = litium2 ? litium2.requestContext : null;
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (requestContext) headers['litium-request-context'] = JSON.stringify(requestContext);

            const response = await meetingFetch('postJson', url, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(body),
            });

            if (response.ok) {
                this.confirmed = true;
                try { return await response.json(); } catch { return null; }
            } else {
                try {
                    const err = await response.json();
                    this.bookingError = (err && err.detail) || (err && err.title) || 'Misslyckades. Försök igen.';
                } catch (parseErr) {
                    this.bookingError = 'Misslyckades. Försök igen.';
                }
                // 409 = the time was taken or closed for booking while the customer filled in the form.
                this.track('booking_failed', 'status ' + response.status + ': ' + this.bookingError);
                return null;
            }
        } catch (e) {
            this.bookingError = 'Misslyckades. Försök igen.';
            this.track('booking_failed', 'no response (network or script error): ' + e);
            return null;
        } finally {
            this.submitting = false;
        }
    }

    handleAdditionalInfoImages(event: Event) {
        const input = event.target as HTMLInputElement;
        this.additionalInfoForm.images = input.files ? Array.from(input.files) : [];
    }

    async submitAdditionalInfo(formData: CustomFieldData[]) {
        if (!this.bookedMeetingId) return;

        this.additionalInfoSubmitting = true;
        this.additionalInfoError = null;

        const colorSamples = this.getFieldValue(formData, websiteText('meeting.color.samples'))
            .split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
        const countertopMaterials = this.getFieldValue(formData, websiteText('meeting.countertop.material'))
            .split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
        const images = this.getFieldFiles(formData, websiteText('meeting.images'));

        const payload = new FormData();
        colorSamples.forEach(v => payload.append('colorSamples[]', v));
        countertopMaterials.forEach(v => payload.append('countertopMaterials[]', v));
        images.forEach(f => {
            if (!f.fileUrl) return;
            payload.append('imageUrls[]', f.fileUrl);
            payload.append('imageFileNames[]', f.originalFileName || f.fileName || '');
        });

        try {
            const litium2 = (window as any).__litium;
            const requestContext = litium2 ? litium2.requestContext : null;
            const headers: Record<string, string> = {};
            if (requestContext) headers['litium-request-context'] = JSON.stringify(requestContext);

            const response = await meetingFetch('store-additionalinfo',
                `/api/publicmeetingbooking/additionalinfo/${this.bookedMeetingId}`,
                { method: 'POST', headers, body: payload }
            );

            if (response.ok) {
                this.additionalInfoDone = true;
            } else {
                this.additionalInfoError = 'Kunde inte skicka informationen. Du kan stänga och försöka igen senare.';
            }
        } catch (e) {
            this.additionalInfoError = 'Kunde inte skicka informationen. Du kan stänga och försöka igen senare.';
        } finally {
            this.additionalInfoSubmitting = false;
        }
    }

    private formatDate(d: Date): string {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return y + '-' + m + '-' + day;
    }
}

export default NewMeetingBookingFlow;
